import type { CSSProperties } from 'react';

/**
 * The type roles.
 *
 * DESIGN.md has named these eight since M5 and the codebase had none of them:
 * every label, readout and identifier was a hand-typed style object, so the
 * system's most-repeated rule — uppercase label text is tracked at `.14em` —
 * was held by 82 separate acts of discipline. The audit found eleven distinct
 * tracking values (including `.1em` and `.10em`, the same computed value
 * written twice, which is what a missing constant looks like) and fifteen font
 * sizes, five of them off any documented ramp.
 *
 * Colour was never the problem: every colour in the console already resolves
 * through `var(--token)`. This is the other half of that.
 *
 * ## They are objects to spread, not components
 *
 * The console styles inline and M5-D2 keeps it that way — design output is
 * presentational, props are protocol types, and a class layer would be a third
 * place to look. So a role is spread and then overridden where a real reason
 * exists:
 *
 * ```ts
 * <span style={{ ...LABEL, color: 'var(--text)' }}>WINDOW</span>
 * <span style={{ ...READOUT, fontSize: 16 }}>{value}</span>
 * ```
 *
 * An override of `fontSize` is ordinary — the roles carry their canonical size
 * and the ramp has real steps. An override of `letterSpacing` is not: that is
 * the rule this module exists to hold, and a departure needs a reason written
 * beside it.
 */

/** Terminal screens and the entry sequence's headings. At most one per screen. */
export const DISPLAY: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-ui)',
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

/**
 * The countdown, the turn number, the orders count.
 *
 * The largest thing on the console and the reason JetBrains Mono ships in the
 * bundle: a count set in condensed jitters when it ticks, and this is the one
 * element watched continuously.
 */
export const READOUT: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-data)',
  fontSize: 23,
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
});

/** The denominator beside a readout — `03/10`. Present, but not the value. */
export const READOUT_OF: CSSProperties = Object.freeze({
  fontSize: 12,
  fontWeight: 400,
  // --text-dim, not --text-disabled: a denominator is live information — the turn
  // count, the orders total — and the disabled tier does not clear 4.5:1 against
  // any of the three panel surfaces. WCAG exempts inert controls, not readouts.
  color: 'var(--text-dim)',
});

/** Tab labels, the commit button's primary line, a panel's loud title. */
export const HEADLINE: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

/** The status row's phase cell and equivalent single-word states. */
export const TITLE: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-ui)',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

/**
 * The workhorse. `TURN`, `PHASE`, `WINDOW`, `ORDERS`, every panel header, every
 * field name. `.14em` is the value that makes the label layer read as one layer
 * across five modules written in five different phases.
 */
export const LABEL: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-ui)',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
});

/** Identifiers and values — `CTC-2E105054`, `BLUE-TEL-BOREA`, coordinates. */
export const DATA: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-data)',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '.04em',
});

/**
 * A mono label: a field name or state token set in the data face because it
 * sits in a column of identifiers. Same `.14em` as `LABEL`, different face.
 */
export const DATA_LABEL: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-data)',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
});

/** The classification bar, correlation ids, vertical rail stubs, placeholders. */
export const DATA_TAG: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-data)',
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
});

/**
 * The only role with a line-height above 1, and the only place a full sentence
 * is permitted. An error's actionable line, an intel entry's prose.
 */
export const BODY: CSSProperties = Object.freeze({
  fontFamily: 'var(--font-prose)',
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.45,
});
